import React from 'react';
import { Box, MenuItem, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import ContentCutOutlinedIcon from '@mui/icons-material/ContentCutOutlined';
import QuickComposer from '../AnnotationStudio/QuickComposer';
import CutComposer from './CutComposer';
import { ALL_ANGLES, angleName } from './multicamStudioModel';

export type ComposerMode = 'note' | 'cut';

interface MulticamComposerProps {
  mode: ComposerMode;
  onModeChange: (mode: ComposerMode) => void;
  angles: Array<{ angleKey: string; label?: string | null }>;
  /** Which angle a new note is about: an angle key, or ALL_ANGLES. */
  noteScope: string;
  onNoteScopeChange: (scope: string) => void;
  disabled?: boolean;
  note: React.ComponentProps<typeof QuickComposer>;
  cut: React.ComponentProps<typeof CutComposer>;
}

/**
 * Buttons in the composers don't take focus from the text field when clicked
 * (keyboard focus still reaches them). The composers drop their hint row when
 * the field blurs; with the page scrolled to the bottom that shrink moved the
 * button out from under the pointer between mousedown and mouseup, and the
 * click was lost. Keeping focus also lets the next note be typed straight away.
 */
const keepFieldFocus = (event: React.MouseEvent) => {
  const target = event.target as HTMLElement | null;
  if (target?.closest('button')) event.preventDefault();
};

/**
 * What is added below the picture: a note (the studio's composer, plus which
 * angle it is about) or a camera cut. One composer at a time, so the view
 * keeps a single primary button.
 */
const MulticamComposer: React.FC<MulticamComposerProps> = ({
  mode,
  onModeChange,
  angles,
  noteScope,
  onNoteScopeChange,
  disabled = false,
  note,
  cut,
}) => {
  const canCut = angles.length > 1;
  return (
    <Box data-testid="multicam-composer" onMouseDownCapture={keepFieldFocus}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', mb: 1.25 }}>
        {canCut && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={mode}
            onChange={(_, value: ComposerMode | null) => value && onModeChange(value)}
            aria-label="What to add"
            disabled={disabled}
            sx={{ height: 36 }}
          >
            <ToggleButton value="note" sx={{ gap: 0.75, px: 1.5, fontWeight: 600 }}>
              <ChatBubbleOutlineOutlinedIcon sx={{ fontSize: 18 }} />
              Note
            </ToggleButton>
            <ToggleButton value="cut" sx={{ gap: 0.75, px: 1.5, fontWeight: 600 }}>
              <ContentCutOutlinedIcon sx={{ fontSize: 18 }} />
              Camera cut
            </ToggleButton>
          </ToggleButtonGroup>
        )}
        {mode === 'note' && angles.length > 0 && (
          <TextField
            select
            size="small"
            label="Applies to"
            value={noteScope}
            onChange={(event) => onNoteScopeChange(String(event.target.value))}
            disabled={disabled}
            sx={{ minWidth: 190, '& .MuiInputBase-root': { height: 36 } }}
          >
            <MenuItem value={ALL_ANGLES}>All angles</MenuItem>
            {angles.map((angle) => (
              <MenuItem key={angle.angleKey} value={angle.angleKey}>
                {angleName(angle)}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Box>
      {mode === 'cut' && canCut ? <CutComposer {...cut} /> : <QuickComposer {...note} />}
    </Box>
  );
};

export default MulticamComposer;
